import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { NavigateFunction } from 'react-router-dom';

import { WalletService } from '@/features/wallets/services/WalletService';

import { closeModal } from '@/store/actions/core.actions';

import { BeneficiaryService } from '@/services/BeneficiaryService';

import { TAppState } from '@/store/reducers/reducers';
import { ICursorElements, IPagination } from '@/store/types/search.types';
import { IProfile } from '@/store/models/profile.model';
import {
  IRestrictionsGroup,
  restrictionsGroupIdProps,
  RestrictionsGroupStatus,
  TRestrictionsGroupType,
} from '@/store/models/restriction.model';
import { ISearchFilters } from '@/store/models/search.model';
import {
  TDashboardBeneficiariyModal,
  TDashboardConfigSettingsBeneficiaries,
  TDashboardConfigSettingsCardsDesignType,
} from '@/store/models/dashboard-config.model';
import { TNavigationAction, NavigationActionTypes } from '@/store/types/navigation.types';
import { TCoreAction } from '@/store/types/modals.types';
import { TAppError } from '@/store/types/base.state';
import { IProfileState, TProfileAction, ProfileActionTypes } from '@/store/types/profile.types';

import {
  TMetaBeneficiary,
  TCreateAndEditBeneficiary,
  IFilterBeneficiary,
  buildBeneficiaryRequest,
  TEditBeneficiary,
  IBeneficiary,
} from '@/features/beneficiaries/store/models/beneficiary.model';

import { RestrictionsTools } from '@/utils/tools/RestrictionsTools';
import { DashboardHelpers } from '@/utils/DashboardHelpers';

import { UserService } from '@/features/user/services/UserService';
import { IUser, IPostUser } from '@/features/user/store/user.model';

import { CardService } from '@/features/cards/services/CardService';
import { CardTransactionService } from '@/features/cards/services/CardTransactionService';
import {
  ITransactionSimulation,
  IToggleCardOptions,
  TCardLimits,
  ICardTransaction,
  IPaymentCard,
  TCardTotalLimits,
  PaymentCardActionName,
  IDigitizedCard,
  TDigitizedCardActions,
  digitizedCardActions,
  IDigitizedCardAction,
  TDigitizedCardReasonCode,
  IEnrollment,
  TCardTransactionFormFilters,
} from '@/features/cards/store/models/card.model';
import { TCardUnitCreationParams } from '@/features/cards/hooks/useInitializeCard';
import { TCardActionsPayloadType } from '@/features/cards/store/types/card.types';

export const addProfile = (filters?: ISearchFilters): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({ type: ProfileActionTypes.ADD_PROFILE });
  const fail = (error: TAppError): TProfileAction => ({ type: ProfileActionTypes.ADD_PROFILE_FAIL, error });
  const success = (profile: IProfile): TProfileAction => ({
    type: ProfileActionTypes.ADD_PROFILE_SUCCESS,
    shouldSkipToaster: true,
    profile,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    try {
      dispatch(request());

      let users: IUser[];
      if (filters?.cardPublicToken) {
        const expectedCards = await CardService.searchCards({ publicToken: filters.cardPublicToken });

        if (expectedCards?.length === 0) return Promise.reject();

        users = await UserService.searchUsers({ userId: expectedCards[0].userId });
      } else if (filters?.walletId) {
        const expectedWallets = await WalletService.searchWallets({ walletId: filters.walletId });

        if (expectedWallets?.length === 0) return Promise.reject();

        users = await UserService.searchUsers({ userId: expectedWallets[0].userId });
      } else {
        users = await UserService.searchUsers(filters);
      }

      if (users.length === 0) return Promise.reject();

      const user = users[0];

      const profile: IProfile = { user: { state: user }, profileWallets: { state: [] }, profileCards: { state: [] } };

      dispatch(success(profile));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const createChildUser = (
  childUserToCreate: IPostUser,
  batchIndex?: string,
  onFinish?: (userId: number) => void,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const requestChildUser = (): TProfileAction => ({
    type: ProfileActionTypes.CREATE_CHILD_USER,
    userId: childUserToCreate.parentUserId as number,
    batchIndex,
  });
  const failChildUser = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.CREATE_CHILD_USER_FAIL,
    userId: childUserToCreate.parentUserId as number,
    error,
    documentation: 'https://docs.treezor.com/guide/users/parent-children.html#key-attributes',
    batchIndex,
  });
  const successChildUser = (user: IUser): TProfileAction => ({
    type: ProfileActionTypes.CREATE_CHILD_USER_SUCCESS,
    shouldSkipToaster: true,
    user,
    batchIndex,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    if (!childUserToCreate.parentUserId) dispatch(failChildUser("This user doesn't have parent"));

    dispatch(requestChildUser());
    try {
      const newUser = await UserService.createUser(childUserToCreate, undefined, batchIndex);
      dispatch(successChildUser(newUser));
      if (onFinish) {
        onFinish(newUser.userId);
      }
    } catch (error) {
      dispatch(failChildUser(error));
    }
  };
};

export const navigateWalletFromCard = (walletId: number, navigate: NavigateFunction) => {
  const request = (): TNavigationAction => ({ type: NavigationActionTypes.NAVIGATE });
  const fail = (error: TAppError): TNavigationAction => ({ type: NavigationActionTypes.NAVIGATE_FAIL, error });
  const success = (): TNavigationAction => ({ type: NavigationActionTypes.NAVIGATE_SUCCESS, shouldSkipToaster: true });

  return async (dispatch: ThunkDispatch<TAppState, {}, TNavigationAction>): Promise<void> => {
    dispatch(request());
    try {
      const wallet = await WalletService.searchWallet(walletId);

      navigate(DashboardHelpers.generateProfilePath(wallet.userId, { walletId: wallet?.walletId }));
      dispatch(success());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const removeProfile = (userId: number): TProfileAction => ({ type: ProfileActionTypes.REMOVE_PROFILE, userId });

export const removeAllProfile = (): TProfileAction => ({ type: ProfileActionTypes.REMOVE_ALL_PROFILE });

export const getBeneficiaries = (
  userId: number,
  filter?: IFilterBeneficiary,
): ThunkAction<Promise<void>, {}, {}, TProfileAction> => {
  const beneficiaryRequest = (): TProfileAction => ({ type: ProfileActionTypes.GET_BENEFICIARIES, userId });
  const beneficiaryFail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.GET_BENEFICIARIES_FAIL,
    userId,
    error,
    documentation: 'https://docs.treezor.com/guide/transfers/beneficiaries.html#structure',
  });
  const beneficiarySuccess = (
    userId: number,
    beneficiaries: IBeneficiary[],
    meta?: TMetaBeneficiary,
    pagination?: IPagination,
  ): TProfileAction => ({
    type: ProfileActionTypes.GET_BENEFICIARIES_SUCCESS,
    shouldSkipToaster: true,
    userId,
    beneficiaries,
    meta,
    pagination,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(beneficiaryRequest());
    try {
      const { beneficiaries, meta } = await BeneficiaryService.getBeneficiaries({ userId, ...filter });
      dispatch(beneficiarySuccess(userId, beneficiaries, { ...meta, isScte: filter?.usableForSct ?? false }));
    } catch (error) {
      dispatch(beneficiaryFail(error));
    }
  };
};

export const createBeneficiary = (
  newBeneficiary: TCreateAndEditBeneficiary,
  beneficiaryType: TDashboardBeneficiariyModal,
): ThunkAction<Promise<void>, {}, {}, TProfileAction | TCoreAction> => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.CREATE_BENEFICIARY,
    userId: newBeneficiary.userId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.CREATE_BENEFICIARY_FAIL,
    userId: newBeneficiary.userId,
    error,
    documentation: 'https://docs.treezor.com/guide/transfers/beneficiaries.html#creation',
  });
  const success = (beneficiary: IBeneficiary): TProfileAction => ({
    type: ProfileActionTypes.CREATE_BENEFICIARY_SUCCESS,
    beneficiary,
    beneficiaryType: beneficiaryType === 'scte' ? 'scte' : 'sddr',
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction | TCoreAction>): Promise<void> => {
    dispatch(request());
    try {
      const builtBeneficiary = buildBeneficiaryRequest(beneficiaryType, newBeneficiary);
      const beneficiary = await BeneficiaryService.createBeneficiary(builtBeneficiary);
      dispatch(success(beneficiary));
      dispatch(closeModal());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const editBeneficiary = (
  beneficiaryToUpdate: TEditBeneficiary,
): ThunkAction<Promise<void>, {}, {}, TProfileAction | TCoreAction> => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.EDIT_BENEFICIARY,
    beneficiary: beneficiaryToUpdate,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.EDIT_BENEFICIARY_FAIL,
    beneficiary: beneficiaryToUpdate,
    error,
    documentation: 'https://docs.treezor.com/guide/transfers/beneficiaries.html#structure',
  });
  const success = (beneficiary: IBeneficiary): TProfileAction => ({
    type: ProfileActionTypes.EDIT_BENEFICIARY_SUCCESS,
    beneficiary,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction | TCoreAction>): Promise<void> => {
    dispatch(request());
    try {
      const beneficiary = await BeneficiaryService.editBeneficiary(beneficiaryToUpdate);

      dispatch(success(beneficiary));
      dispatch(closeModal());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const updateBeneficiaryStatus = (
  beneficiaryToUpdate: TEditBeneficiary & { isActive: boolean },
  beneficiaryType: TDashboardConfigSettingsBeneficiaries,
): ThunkAction<Promise<void>, {}, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_BENEFICIARY_STATUS,
    beneficiary: beneficiaryToUpdate,
    beneficiaryType,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_BENEFICIARY_STATUS_FAIL,
    beneficiary: beneficiaryToUpdate,
    beneficiaryType,
    error,
    documentation: 'https://docs.treezor.com/guide/transfers/beneficiaries.html#structure',
  });
  const success = (beneficiary: IBeneficiary): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_BENEFICIARY_STATUS_SUCCESS,
    beneficiary,
    beneficiaryType,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const beneficiary = await BeneficiaryService.editBeneficiary(beneficiaryToUpdate);

      dispatch(success(beneficiary));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getCards = (userId: number): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({ type: ProfileActionTypes.GET_CARDS, userId });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.GET_CARDS_FAIL,
    userId,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/introduction.html',
  });
  const success = (cards: IPaymentCard[]): TProfileAction => ({
    type: ProfileActionTypes.GET_CARDS_SUCCESS,
    shouldSkipToaster: true,
    userId,
    cards,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(unsetCards(userId));
    dispatch(request());
    try {
      const cards = await CardService.searchCards({ userId });
      dispatch(success(cards));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getCurrentCardTransactions = (
  cardId: number,
  userId: number,
  filters?: TCardTransactionFormFilters,
  cursor: string | null = null,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({ type: ProfileActionTypes.GET_CURRENT_CARD_TRANSACTIONS, userId, cardId });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.GET_CURRENT_CARD_TRANSACTIONS_FAIL,
    userId,
    cardId,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/transactions.html',
  });
  const success = (paginateCardTransactions: ICursorElements<ICardTransaction>, userId: number): TProfileAction => ({
    type: ProfileActionTypes.GET_CURRENT_CARD_TRANSACTIONS_SUCCESS,
    shouldSkipToaster: true,
    paginateCardTransactions,
    cardId,
    userId,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const paginateCardTransactions = await CardTransactionService.getCardTransactionsById(cardId, filters, cursor);
      dispatch(success(paginateCardTransactions, userId));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const updatePaymentCardForLimits = (
  cardId: number,
  userId: number,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({ type: ProfileActionTypes.UPDATE_PAYMENT_CARD_LIMITS, userId, cardId });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_PAYMENT_CARD_LIMITS_FAIL,
    userId,
    cardId,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#payments-withdrawals-limits',
  });
  const success = (cardLimits: TCardTotalLimits, userId: number): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_PAYMENT_CARD_LIMITS_SUCCESS,
    shouldSkipToaster: true,
    cardLimits,
    userId,
    cardId,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const { elements } = await CardTransactionService.getCardTransactionsById(cardId, {}, null);
      dispatch(success(elements[0], userId));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const setCurrentCardId = (userId: number, currentCardId: number): TProfileAction => ({
  type: ProfileActionTypes.SET_CURRENT_CARD,
  userId,
  currentCardId,
});

export const unsetCards = (userId: number): TProfileAction => ({ type: ProfileActionTypes.UNSET_CARDS, userId });

export const toggleOptions = (
  userId: number,
  cardId: number,
  options: IToggleCardOptions,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({ type: ProfileActionTypes.UPDATE_CARD_OPTIONS, userId, cardId });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_CARD_OPTIONS_FAIL,
    cardId,
    userId,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#options-change',
  });
  const success = (card: IPaymentCard): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_CARD_OPTIONS_SUCCESS,
    card,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const cardResponse = await CardService.toggleOptions(cardId, options);
      dispatch(success({ ...cardResponse, cardId }));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const showSetPinActions = (
  userId: number,
  cardId: number,
  action: PaymentCardActionName,
  hasPayload: boolean,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const toggle = (): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_CARD_ACTIONS_SETPIN,
    cardId,
    userId,
    action,
    hasPayload,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(toggle());
  };
};

export const setCardActions = (
  userId: number,
  cardId: number,
  action: PaymentCardActionName,
  payload: TCardActionsPayloadType,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({ type: ProfileActionTypes.UPDATE_CARD_ACTIONS, cardId, userId, action });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_CARD_ACTIONS_FAIL,
    cardId,
    userId,
    modifiedAction: action,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#overview-diagram',
  });
  const success = (card: IPaymentCard): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_CARD_ACTIONS_SUCCESS,
    action,
    card,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      let cardResponse: IPaymentCard;
      if (action === PaymentCardActionName.LIMITS) {
        cardResponse = await CardService.setCardLimits(cardId, payload as IPaymentCard | TCardLimits<string | number>);
      } else if (action === PaymentCardActionName.UNBLOCK_CVC) {
        await CardService.unblockCvc(cardId, action);
        cardResponse = payload as IPaymentCard;
      } else {
        const actionSentToService =
          action === PaymentCardActionName.UNLOCK || action === PaymentCardActionName.LOCK
            ? PaymentCardActionName.LOCKUNLOCK
            : action;

        cardResponse = await CardService.setCardActions(cardId, actionSentToService, payload);
      }
      dispatch(success({ ...cardResponse, cardId }));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const enrollCard = (
  userId: number,
  cardId: number,
  action: PaymentCardActionName.REGISTER_3DS_OTPSMS | PaymentCardActionName.REGISTER_3DS_OOB,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({ type: ProfileActionTypes.ENROLL_CARD, cardId, userId });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.ENROLL_CARD_FAIL,
    cardId,
    userId,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#overview-diagram',
  });
  const success = (method: IEnrollment): TProfileAction => ({
    type: ProfileActionTypes.ENROLL_CARD_SUCCESS,
    method,
    cardId,
    userId,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      let cardResponse = await CardService.enrollCard(cardId, action);
      if (action === PaymentCardActionName.REGISTER_3DS_OTPSMS)
        cardResponse = { id: '', publicToken: '', system: '', method: 'OTPSMS' };
      dispatch(success(cardResponse));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const blockCard = (
  userId: number,
  cardId: number,
  lockStatus: number,
  action: PaymentCardActionName,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({ type: ProfileActionTypes.BLOCK_CARD, userId, cardId, blockAction: action });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.BLOCK_CARD_FAIL,
    userId,
    cardId,
    error,
    blockAction: action,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#block-a-card',
  });
  const success = (card: IPaymentCard): TProfileAction => ({
    type: ProfileActionTypes.BLOCK_CARD_SUCCESS,
    userId,
    card,
    blockAction: action,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const card = await CardService.blockCard(cardId, lockStatus);
      dispatch(success(card));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const createCard = (
  card: TCardUnitCreationParams,
  type: TDashboardConfigSettingsCardsDesignType,
  onFinish?: (cardId: number) => void,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({ type: ProfileActionTypes.CREATE_CARD, userId: card.userId });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.CREATE_CARD_FAIL,
    userId: card.userId,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#overview-diagram',
  });
  const success = (newCard: IPaymentCard): TProfileAction => ({
    type: ProfileActionTypes.CREATE_CARD_SUCCESS,
    userId: card.userId,
    card: newCard,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const newCard = await CardService.createCard(card, type);
      dispatch(success(newCard));
      onFinish?.(newCard.cardId);
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const updateCard = (
  userId: number,
  cardId: number,
  card: Partial<IPaymentCard>,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({ type: ProfileActionTypes.UPDATE_CARD, userId, cardId });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_CARD_FAIL,
    userId,
    cardId,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#overview-diagram',
  });
  const success = (card: IPaymentCard): TProfileAction => ({ type: ProfileActionTypes.UPDATE_CARD_SUCCESS, card });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const cardUpdated = await CardService.updateCard(cardId, card);

      dispatch(success(cardUpdated));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const setCardLimits = (
  userId: number,
  cardId: number,
  limits: TCardLimits,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({ type: ProfileActionTypes.UPDATE_CARD_LIMITS, userId, cardId });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_CARD_LIMITS_FAIL,
    userId,
    cardId,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#payments-withdrawals-limits',
  });
  const success = (card: IPaymentCard): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_CARD_LIMITS_SUCCESS,
    card,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const cardLimits = await CardService.setCardLimits(cardId, limits);
      dispatch(success(cardLimits));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getCardEnrollment = (
  cardId: number,
  userId: number,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({ type: ProfileActionTypes.GET_CARD_ENROLLMENT, userId, cardId });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.GET_CARD_ENROLLMENT_FAIL,
    userId,
    cardId,
    error,
  });
  const success = (enrollments: IEnrollment[]): TProfileAction => ({
    type: ProfileActionTypes.GET_CARD_ENROLLMENT_SUCCESS,
    shouldSkipToaster: true,
    cardId,
    userId,
    enrollments,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const enrollments = await CardService.getCardEnrollment(cardId);
      dispatch(success(enrollments));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const deleteOOBEnrollment = (
  cardId: number,
  userId: number,
  authMethodId: string,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.DELETE_OOB_CARD_ENROLLMENT,
    userId,
    cardId,
    authMethodId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.DELETE_OOB_CARD_ENROLLMENT_FAIL,
    userId,
    cardId,
    error,
  });
  const success = (): TProfileAction => ({
    type: ProfileActionTypes.DELETE_OOB_CARD_ENROLLMENT_SUCCESS,
    cardId,
    userId,
    authMethodId,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      await CardService.deleteOOBEnrollment(cardId, authMethodId);
      dispatch(success());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const simulateCardTransactions = (
  userId: number,
  cardId: number,
  transaction: ITransactionSimulation & { publicToken: string },
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction | TCoreAction> => {
  const request = (): TProfileAction => ({ type: ProfileActionTypes.SIMULATE_TRANSACTION, userId, cardId });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.SIMULATE_TRANSACTION_FAIL,
    userId,
    cardId,
    error,
  });
  const success = (): TProfileAction => ({ type: ProfileActionTypes.SIMULATE_TRANSACTION_SUCCESS, userId, cardId });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction | TCoreAction>): Promise<void> => {
    dispatch(request());
    try {
      await CardService.simulateCardTransactions(transaction);
      dispatch(success());
      dispatch(closeModal());
      dispatch(getCurrentCardTransactions(cardId, userId));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getCustomRestrictionsGroup = (
  userId: number,
  cardId: number,
  restrictionsGroupId: number,
  restrictionsGroupType: TRestrictionsGroupType,
): ThunkAction<Promise<TProfileAction>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.GET_CUSTOM_RESTRICTIONS_GROUP,
    userId,
    cardId,
    restrictionsGroupType,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.GET_CUSTOM_RESTRICTIONS_GROUP_FAIL,
    userId,
    cardId,
    restrictionsGroupType,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#list-based-restrictions',
  });
  const success = (
    restrictionsGroup: IRestrictionsGroup,
    restrictionsGroupType: TRestrictionsGroupType,
  ): TProfileAction => ({
    type: ProfileActionTypes.GET_CUSTOM_RESTRICTIONS_GROUP_SUCCESS,
    userId,
    cardId,
    restrictionsGroup,
    restrictionsGroupType,
    shouldSkipToaster: true,
  });

  return async (
    dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>,
    _: () => IProfileState,
  ): Promise<TProfileAction> => {
    dispatch(request());
    try {
      const baaSRestrictionsGroup = await CardService.searchCustomRestrictionsGroup(
        restrictionsGroupId,
        restrictionsGroupType,
      );

      const restrictionsGroup = RestrictionsTools.convertToRestrictionsGroup(
        baaSRestrictionsGroup,
        restrictionsGroupType,
      );
      return dispatch(success(restrictionsGroup, restrictionsGroup.type));
    } catch (error) {
      return dispatch(fail(error));
    }
  };
};

export const addCustomRestrictionsGroup = (
  userId: number,
  cardId: number,
  newRestrictionsGroup: IRestrictionsGroup,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.ADD_CUSTOM_RESTRICTIONS_GROUP,
    userId,
    cardId,
    restrictionsGroupType: newRestrictionsGroup.type,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.ADD_CUSTOM_RESTRICTIONS_GROUP_FAIL,
    userId,
    cardId,
    restrictionsGroupType: newRestrictionsGroup.type,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#list-based-restrictions',
  });
  const success = (restrictionsGroup: IRestrictionsGroup): TProfileAction => ({
    type: ProfileActionTypes.ADD_CUSTOM_RESTRICTIONS_GROUP_SUCCESS,
    userId,
    cardId,
    restrictionsGroup,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());

    try {
      const baaSRestrictionsGroup = RestrictionsTools.convertToBaaSRestrictionsGroup(newRestrictionsGroup);
      const baaSRestrictionsGroupAdded = await CardService.addCustomRestrictionsGroup(
        { ...baaSRestrictionsGroup, status: RestrictionsGroupStatus.VALIDATED },
        newRestrictionsGroup.type,
      );

      const restrictionsGroup = RestrictionsTools.convertToRestrictionsGroup(
        baaSRestrictionsGroupAdded,
        newRestrictionsGroup.type,
      );

      if (restrictionsGroup.id) {
        dispatch(
          updateCard(
            userId,
            cardId,
            RestrictionsTools.populateCardRestrictionsGroupId(restrictionsGroup.id, newRestrictionsGroup.type),
          ),
        );
      }

      dispatch(success(restrictionsGroup));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const addPredefToCardRestrictionsgroups = (
  userId: number,
  cardId: number,
  newPredefRestrictionsGroup: IRestrictionsGroup,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.ADD_CUSTOM_RESTRICTIONS_GROUP,
    userId,
    cardId,
    restrictionsGroupType: newPredefRestrictionsGroup.type,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.ADD_CUSTOM_RESTRICTIONS_GROUP_FAIL,
    userId,
    cardId,
    restrictionsGroupType: newPredefRestrictionsGroup.type,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#list-based-restrictions',
  });
  const success = (restrictionsGroup: IRestrictionsGroup): TProfileAction => ({
    type: ProfileActionTypes.ADD_CUSTOM_RESTRICTIONS_GROUP_SUCCESS,
    userId,
    cardId,
    restrictionsGroup,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());

    try {
      dispatch(
        updateCard(userId, cardId, {
          [restrictionsGroupIdProps[newPredefRestrictionsGroup.type]]: newPredefRestrictionsGroup.id,
        }),
      );

      dispatch(success(newPredefRestrictionsGroup));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const updateCustomRestrictionsGroup = (
  userId: number,
  cardId: number,
  newRestrictionsGroup: IRestrictionsGroup,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_CUSTOM_RESTRICTIONS_GROUP,
    userId,
    cardId,
    restrictionsGroupType: newRestrictionsGroup.type,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_CUSTOM_RESTRICTIONS_GROUP_FAIL,
    userId,
    cardId,
    restrictionsGroupType: newRestrictionsGroup.type,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#list-based-restrictions',
  });
  const success = (restrictionsGroup: IRestrictionsGroup): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_CUSTOM_RESTRICTIONS_GROUP_SUCCESS,
    userId,
    cardId,
    restrictionsGroup,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    const baaSRestrictionsGroup = RestrictionsTools.convertToBaaSRestrictionsGroup(newRestrictionsGroup);
    try {
      const baaSRestrictionsGroupAdded = await CardService.updateCustomRestrictionsGroup(
        baaSRestrictionsGroup,
        newRestrictionsGroup.type,
      );

      const restrictionsGroup = RestrictionsTools.convertToRestrictionsGroup(
        baaSRestrictionsGroupAdded,
        newRestrictionsGroup.type,
      );

      dispatch(success(restrictionsGroup));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const deleteCustomRestrictionsGroup = (
  paymentCard: IPaymentCard,
  restrictionsGroupType: TRestrictionsGroupType,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.DELETE_CUSTOM_RESTRICTIONS_GROUP,
    userId: paymentCard.userId,
    cardId: paymentCard.cardId,
    restrictionsGroupType,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.DELETE_CUSTOM_RESTRICTIONS_GROUP_FAIL,
    userId: paymentCard.userId,
    cardId: paymentCard.cardId,
    restrictionsGroupType,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#list-based-restrictions',
  });
  const success = (card: IPaymentCard): TProfileAction => ({
    type: ProfileActionTypes.DELETE_CUSTOM_RESTRICTIONS_GROUP_SUCCESS,
    userId: paymentCard.userId,
    cardId: paymentCard.cardId,
    restrictionsGroupType,
    card,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const updatedCard = await CardService.deleteCardRestrictionsGroup(paymentCard.cardId, {
        [restrictionsGroupIdProps[restrictionsGroupType]]: 0,
      });

      dispatch(success(updatedCard));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getDigitizedCards = (
  cardId: number,
  userId: number,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({ type: ProfileActionTypes.GET_DIGITIZED_CARDS, cardId, userId });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.GET_DIGITIZED_CARDS_FAIL,
    cardId,
    userId,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/introduction.html',
  });
  const success = (cards: IDigitizedCard[]): TProfileAction => ({
    type: ProfileActionTypes.GET_DIGITIZED_CARDS_SUCCESS,
    shouldSkipToaster: true,
    cardId,
    userId,
    cards,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const cards = await CardService.getDigitizedCards(cardId);
      dispatch(success(cards));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export function setDigitizedCardActions(
  cardId: number,
  digitizedCardId: string,
  action: TDigitizedCardActions,
  reasonCode: TDigitizedCardReasonCode,
  userId: number,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_DIGITIZED_CARDS_ACTIONS,
    cardId,
    digitizedCardId,
    action,
    reasonCode,
    userId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_DIGITIZED_CARDS_ACTIONS_FAIL,
    cardId,
    digitizedCardId,
    action,
    error,
    userId,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#overview-diagram',
  });
  const success = ({
    id: digitizedCardId,
    cardId,
    status,
    createdDate = undefined,
    modifiedDate = undefined,
  }): TProfileAction => ({
    type: ProfileActionTypes.UPDATE_DIGITIZED_CARDS_ACTIONS_SUCCESS,
    digitizedCardId,
    cardId: parseInt(cardId),
    status,
    userId,
    createdDate,
    modifiedDate,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      let cardResponse: IDigitizedCardAction;
      if (action === digitizedCardActions.DELETE) {
        cardResponse = await CardService.setDigitizedCarAction(digitizedCardId, action, reasonCode);
      } else if ([digitizedCardActions.SUSPEND, digitizedCardActions.UNSUSPEND].includes(action)) {
        cardResponse = await CardService.setDigitizedCarAction(digitizedCardId, action, reasonCode);
      } else {
        throw `${action} : This action is not allowed`;
      }
      dispatch(success({ ...cardResponse }));
    } catch (error) {
      dispatch(fail(error));
    }
  };
}

export const setCurrentRestrictionsGroup = (
  userId: number,
  cardId: number,
  restrictionsGroupType: TRestrictionsGroupType,
): TProfileAction => ({
  type: ProfileActionTypes.SET_CURRENT_RESTRICTIONS_GROUP,
  cardId,
  userId,
  restrictionsGroupType,
});
